import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { keyframes } from '@emotion/react';
import agent from '../../agent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import FreakLocalizationTracker from './FreakLocalizationTracker';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BOTTOM_NAVIGATION_VISIBILITY,
  RECORDING_FREAK_ROUTE,
  TOP_NAVIGATION_VISIBILITY,
} from '../../constants/actionTypes';
import { calculateDistance, getDistanceFromLatLonInMeters } from '../../utils/freakUtils';
import {
  avgPace, avgSpeedKmh, formatClock, formatPace, gpsQuality, paceFromSpeed, usesPace,
} from '../../utils/trackStats';
import useTranslation from '../../customHooks/translations';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const ROUTE_BATCH_LIMIT = 10;
const MIN_DISTANCE_METERS = 2;

const PANEL_BG = '#0d1b2a';
const MUTED = '#8a9bb0';
const LINE = '#24364b';
const RECORDING_COLOR = '#ff4d4f';
const PAUSED_COLOR = '#f5a623';
const FINISH_COLOR = '#ff5a36';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.25; }
`;

// stored as text on the activity
const formatHMS = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const overlaySx = {
  bgcolor: 'rgba(13,27,42,0.85)',
  backdropFilter: 'blur(8px)',
  color: 'white',
  boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
};

const roundButtonSx = {
  ...overlaySx,
  width: 44,
  height: 44,
  '&:hover': { bgcolor: 'rgba(13,27,42,0.95)' },
};

const StatBlock = ({ value, unit, label, large, testId }) => (
  <Box data-testid={testId} sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
    <Typography sx={{
      color: 'white', fontWeight: 800, fontSize: large ? 44 : 30, lineHeight: 1.1,
      fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
    }}>
      {value}
      <Box component="span" sx={{ color: MUTED, fontSize: 15, fontWeight: 600, ml: 0.5 }}>{unit}</Box>
    </Typography>
    <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', mt: 0.25 }}>
      {label}
    </Typography>
  </Box>
);

const StatsDivider = () => <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: LINE }}/>;

const ControlButton = ({ icon, label, color, onClick, size = 80, testId }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
    <ButtonBase
      data-testid={testId}
      aria-label={label}
      onClick={onClick}
      sx={{
        width: size, height: size, borderRadius: '50%', bgcolor: color, color: 'white',
        mt: size < 80 ? 1.5 : 0,
        boxShadow: size < 80 ? 'none' : '0 6px 18px rgba(0,0,0,0.4)',
        transition: 'transform 0.12s, filter 0.12s',
        '&:hover': { filter: 'brightness(1.1)' },
        '&:active': { transform: 'scale(0.95)' },
        '& svg': { fontSize: size < 80 ? 26 : 38 },
      }}
    >
      {icon}
    </ButtonBase>
    <Typography sx={{ color: 'white', fontSize: 13, fontWeight: 700 }}>{label}</Typography>
  </Box>
);

// Strava-style recorder: full-screen map that follows you, a status chip on top,
// a dark stats panel (click the handle for full-screen stats) and big round controls.
// Mirrors mobile screens/Track/TrackScreen.tsx.
const FreakTrackerView = (props) => {
  const translations = useTranslation();
  const tr = translations.freakTrackerView;
  const navigate = useNavigate();
  const locationState = useLocation();
  const currentFreak = locationState?.state?.freak || {};
  const initPosition = locationState?.state?.position || null;

  const context = props.context || props?.currentUser?.context || 'wof';
  const showPace = usesPace(currentFreak?.context || context);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [tracking, setTracking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [gpsPosition, setGpsPosition] = useState(
    initPosition ? { latitude: initPosition[0], longitude: initPosition[1] } : null,
  );
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [routeCoords, setRouteCoords] = useState([]);
  const [distance, setDistance] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [following, setFollowing] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [freakActivity, setFreakActivity] = useState(currentFreak?.freakActivity || null);
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm }

  // ── Refs (read inside geolocation callback — no stale closures) ───────────
  const trackingRef = useRef(false);
  const routeCoordsRef = useRef([]);
  const batchBufferRef = useRef([]);
  const distanceRef = useRef(0);
  const maxSpeedRef = useRef(0);
  const speedCountRef = useRef(0);
  const avgSpeedAccRef = useRef(0);
  const altitudeRef = useRef(0);
  const maxAltitudeRef = useRef(0);
  const elapsedRef = useRef(0);
  const startTimeRef = useRef(null);
  const freakActivityRef = useRef(currentFreak?.freakActivity || null);
  const watchIdRef = useRef(null);
  const prevPosRef = useRef(null);
  const prevTimestampRef = useRef(null);

  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(300);

  // ── Keep trackingRef in sync ──────────────────────────────────────────────
  useEffect(() => { trackingRef.current = tracking; }, [tracking]);

  // ── Recenter button sits just above the collapsed stats panel ─────────────
  useEffect(() => {
    if (!expanded && panelRef.current) setPanelHeight(panelRef.current.offsetHeight);
  });

  // ── Hide nav for the whole tracker session ────────────────────────────────
  useEffect(() => {
    props.setBottomNavigationVisible(false);
    props.setTopNavigationVisible(false);
    return () => {
      props.setBottomNavigationVisible(true);
      props.setTopNavigationVisible(true);
    };
  }, []);

  // ── Timer — starts/pauses with `tracking` ─────────────────────────────────
  useEffect(() => {
    if (!tracking) return;
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now() - elapsedRef.current * 1000;
    }
    const id = setInterval(() => {
      const s = Math.floor((Date.now() - startTimeRef.current) / 1000);
      elapsedRef.current = s;
      setElapsed(s);
    }, 1000);
    return () => clearInterval(id);
  }, [tracking]);

  // ── Geolocation watcher — registered once on mount ────────────────────────
  useEffect(() => {
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, speed: rawSpeed, altitude: rawAlt, accuracy } = pos.coords;

        // Always track GPS position for the live map center
        setGpsPosition({ latitude, longitude });
        setGpsAccuracy(accuracy ?? null);

        if (!trackingRef.current) return;

        // ── Speed ──────────────────────────────────────────────────────────
        let speedKmh = 0;
        const now = pos.timestamp;
        if (rawSpeed != null && rawSpeed >= 0) {
          speedKmh = parseFloat((rawSpeed * 3.6).toFixed(1));
        } else if (prevPosRef.current && prevTimestampRef.current) {
          const dt = (now - prevTimestampRef.current) / 1000;
          if (dt > 0) {
            const dm = getDistanceFromLatLonInMeters(
              prevPosRef.current.latitude, prevPosRef.current.longitude, latitude, longitude,
            );
            speedKmh = parseFloat(((dm / dt) * 3.6).toFixed(1));
          }
        }
        prevPosRef.current = { latitude, longitude };
        prevTimestampRef.current = now;

        setSpeed(speedKmh);
        if (speedKmh > maxSpeedRef.current) maxSpeedRef.current = speedKmh;

        speedCountRef.current += 1;
        avgSpeedAccRef.current = parseFloat(
          ((avgSpeedAccRef.current * (speedCountRef.current - 1) + speedKmh) / speedCountRef.current).toFixed(1),
        );

        // ── Altitude ──────────────────────────────────────────────────────
        if (rawAlt != null) {
          const alt = Math.round(rawAlt);
          altitudeRef.current = alt;
          if (alt > maxAltitudeRef.current) maxAltitudeRef.current = alt;
        }

        // ── Route & distance ──────────────────────────────────────────────
        const prev = routeCoordsRef.current;
        if (prev.length > 0) {
          const last = prev[prev.length - 1];
          const dm = getDistanceFromLatLonInMeters(last[0], last[1], latitude, longitude);
          if (dm < MIN_DISTANCE_METERS) return; // filter micro-movements
          const dk = calculateDistance(last[0], last[1], latitude, longitude);
          distanceRef.current = parseFloat((distanceRef.current + dk).toFixed(3));
          setDistance(distanceRef.current);
        }

        const newCoord = [latitude, longitude];
        const newRoute = [...prev, newCoord];
        routeCoordsRef.current = newRoute;
        setRouteCoords([...newRoute]);

        // ── Batch upload to API ───────────────────────────────────────────
        if (currentFreak?.slug) {
          const newBatch = [...batchBufferRef.current, newCoord];
          batchBufferRef.current = newBatch;
          if (newBatch.length >= ROUTE_BATCH_LIMIT) {
            agent.Freaks.updateRoute(currentFreak.slug, newBatch);
            batchBufferRef.current = [];
          }
        }
      },
      (err) => console.error('Geolocation error:', err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
    );

    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []); // register once

  // ── Helpers ───────────────────────────────────────────────────────────────
  const flushBatch = () => {
    if (batchBufferRef.current.length > 0 && currentFreak?.slug) {
      agent.Freaks.updateRoute(currentFreak.slug, batchBufferRef.current);
      batchBufferRef.current = [];
    }
  };

  const syncActivity = () => {
    const act = freakActivityRef.current;
    if (!act?.slug) return;
    agent.FreakActivities.update({
      slug: act.slug,
      type: context,
      distance: distanceRef.current,
      duration: formatHMS(elapsedRef.current),
      maxSpeed: maxSpeedRef.current,
      avgSpeed: avgSpeedAccRef.current,
      altitude: altitudeRef.current,
      maxAltitude: maxAltitudeRef.current,
    });
  };

  const createActivity = async () => {
    if (!currentFreak?.id) return null;
    const result = await agent.FreakActivities.create({
      freakId: currentFreak.id,
      type: context,
      distance: 0,
      duration: '00:00:00',
      maxSpeed: 0,
      avgSpeed: 0,
      altitude: 0,
      maxAltitude: 0,
    });
    const act = result?.freakActivity;
    freakActivityRef.current = act;
    setFreakActivity(act);
    return act;
  };

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleStart = async () => {
    setTracking(true);
    setPaused(false);
    startTimeRef.current = null; // will be set by timer effect on next tick
    if (!freakActivityRef.current) await createActivity();
  };

  // instant, like other trackers — nothing is lost on pause
  const handlePause = () => {
    setTracking(false);
    setPaused(true);
    setSpeed(0);
    flushBatch();
    props.setRecordingFreakRoute(routeCoordsRef.current);
  };

  const handleResume = () => {
    setTracking(true);
    setPaused(false);
  };

  const handleFinish = () => {
    setTracking(false);
    setPaused(false);
    const finalRoute = routeCoordsRef.current;
    flushBatch();
    if (finalRoute.length > 0 && currentFreak?.slug) {
      agent.Freaks.updateRoute(currentFreak.slug, finalRoute, true);
    }
    props.setRecordingFreakRoute(finalRoute);
    syncActivity();
    navigate(`/freak/${currentFreak.slug}`);
  };

  const handleClear = () => {
    setTracking(false);
    setPaused(false);
    setRouteCoords([]);
    setDistance(0);
    setSpeed(0);
    setElapsed(0);
    routeCoordsRef.current = [];
    batchBufferRef.current = [];
    distanceRef.current = 0;
    maxSpeedRef.current = 0;
    speedCountRef.current = 0;
    avgSpeedAccRef.current = 0;
    altitudeRef.current = 0;
    maxAltitudeRef.current = 0;
    elapsedRef.current = 0;
    startTimeRef.current = null;
    prevPosRef.current = null;
    prevTimestampRef.current = null;
  };

  const showConfirm = (title, message, onConfirm) => setConfirm({ title, message, onConfirm });
  const hideConfirm = () => setConfirm(null);

  // ── Derived ───────────────────────────────────────────────────────────────
  const mapPosition = gpsPosition
    ? [gpsPosition.latitude, gpsPosition.longitude]
    : initPosition;

  const hasStarted = tracking || paused;
  const status = tracking ? 'recording' : paused ? 'paused' : null;
  const gps = gpsQuality(gpsAccuracy);
  const statusLabel = status
    ? tr[status]
    : tr[gps === 'good' ? 'gpsGood' : gps === 'weak' ? 'gpsWeak' : 'gpsSearching'];
  const statusColor = status === 'recording'
    ? RECORDING_COLOR
    : status === 'paused' ? PAUSED_COLOR : gps === 'good' ? DEFAULT_THEME_COLOR : gps === 'weak' ? PAUSED_COLOR : MUTED;

  const confirmFinish = () => showConfirm(tr.finishTitle, tr.finishConfirm, handleFinish);
  const confirmDiscard = () => showConfirm(tr.discardTitle, tr.clearConfirm, handleClear);

  return (
    <Box sx={{ height: '100dvh', background: PANEL_BG, overflow: 'hidden', position: 'relative' }}>

      {/* Map — full screen behind the overlays */}
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <FreakLocalizationTracker
          routeCoordinates={routeCoords}
          position={mapPosition}
          isRunning={tracking}
          follow={following}
          onUserPan={() => setFollowing(false)}
          routeCasing
          liveDot
          hideZoomControl
          zoom={16}
          height="100%"
        />
      </Box>

      {/* Top bar */}
      <Box sx={{
        position: 'absolute', top: 12, left: 12, right: 12, zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {hasStarted
          ? <Box sx={{ width: 44 }}/>
          : <IconButton
            data-testid="track-back"
            aria-label={tr.back}
            onClick={() => navigate(`/freak/${currentFreak.slug}`)}
            sx={roundButtonSx}
          >
            <ArrowBackIosNewOutlinedIcon fontSize="small"/>
          </IconButton>}
        <Box data-testid="track-status" sx={{
          ...overlaySx, display: 'flex', alignItems: 'center', gap: 1, px: 1.75, height: 36, borderRadius: 18,
        }}>
          <Box sx={{
            width: 10, height: 10, borderRadius: '50%', bgcolor: statusColor,
            animation: status === 'recording' ? `${pulse} 1.4s ease-in-out infinite` : 'none',
          }}/>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{statusLabel}</Typography>
        </Box>
        <Box sx={{ width: 44 }}/>
      </Box>

      {/* Recenter — shown after the user pans away */}
      {!following && !expanded && (
        <IconButton
          data-testid="track-recenter"
          aria-label={tr.recenter}
          onClick={() => setFollowing(true)}
          sx={{ ...roundButtonSx, position: 'absolute', right: 16, bottom: panelHeight + 12, zIndex: 1100 }}
        >
          <MyLocationIcon/>
        </IconButton>
      )}

      {/* Stats panel + controls */}
      <Box
        data-testid="track-panel"
        ref={panelRef}
        sx={{
          position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 1100,
          top: expanded ? 0 : 'auto',
          display: 'flex', flexDirection: 'column', justifyContent: expanded ? 'space-between' : 'flex-start',
          bgcolor: PANEL_BG,
          borderRadius: expanded ? 0 : '28px 28px 0 0',
          boxShadow: '0 -6px 24px rgba(0,0,0,0.35)',
          px: 2.5, pt: expanded ? 9 : 0, pb: 'calc(20px + env(safe-area-inset-bottom))',
          maxWidth: 640, mx: 'auto',
        }}
      >
        <ButtonBase
          data-testid="track-panel-toggle"
          aria-label={expanded ? tr.showMap : tr.showStats}
          onClick={() => setExpanded((value) => !value)}
          sx={{ alignSelf: 'center', pt: 1.25, pb: 0.75, px: 3, borderRadius: 2 }}
        >
          {expanded
            ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: MUTED }}>
              <MapOutlinedIcon sx={{ fontSize: 16 }}/>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{tr.showMap}</Typography>
            </Box>
            : <Box sx={{ width: 40, height: 5, borderRadius: 3, bgcolor: '#34465c' }}/>}
        </ButtonBase>

        <Box sx={{ textAlign: 'center', mt: 0.5 }}>
          <Typography data-testid="track-time" sx={{
            color: 'white', fontWeight: 800, fontSize: expanded ? 84 : 52, lineHeight: 1.05,
            letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
          }}>
            {formatClock(elapsed)}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {tr.time}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mt: expanded ? 0 : 1.75, py: expanded ? 3 : 1.5, borderTop: `1px solid ${LINE}` }}>
          <StatBlock testId="track-distance" value={distance.toFixed(2)} unit="km" label={tr.distance} large={expanded}/>
          <StatsDivider/>
          {showPace
            ? <StatBlock testId="track-pace" value={formatPace(paceFromSpeed(speed))} unit="/km" label={tr.pace} large={expanded}/>
            : <StatBlock testId="track-speed" value={speed || 0} unit="km/h" label={tr.speed} large={expanded}/>}
        </Box>

        {expanded && (
          <Box data-testid="track-more-stats" sx={{ display: 'flex', alignItems: 'center', py: 3, borderTop: `1px solid ${LINE}` }}>
            {showPace
              ? <StatBlock value={formatPace(avgPace(distance, elapsed))} unit="/km" label={tr.avgPace}/>
              : <StatBlock value={avgSpeedKmh(distance, elapsed)} unit="km/h" label={tr.avgSpeed}/>}
            <StatsDivider/>
            <StatBlock value={maxSpeedRef.current || 0} unit="km/h" label={tr.maxSpeed}/>
            <StatsDivider/>
            <StatBlock value={maxAltitudeRef.current || 0} unit="m" label={tr.elevation}/>
          </Box>
        )}

        {/* Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 3.5, mt: 1.25 }}>
          {!hasStarted && (
            <ControlButton testId="track-start" icon={<PlayArrowIcon/>} label={tr.start} color={DEFAULT_THEME_COLOR} onClick={handleStart}/>
          )}
          {tracking && (
            <ControlButton testId="track-pause" icon={<PauseIcon/>} label={tr.pause} color="#1f2f42" onClick={handlePause}/>
          )}
          {paused && (
            <>
              <ControlButton testId="track-discard" icon={<DeleteOutlineIcon/>} label={tr.discard} color="#1f2f42" size={56} onClick={confirmDiscard}/>
              <ControlButton testId="track-resume" icon={<PlayArrowIcon/>} label={tr.resume} color={DEFAULT_THEME_COLOR} onClick={handleResume}/>
              <ControlButton testId="track-finish" icon={<SportsScoreIcon/>} label={tr.finish} color={FINISH_COLOR} onClick={confirmFinish}/>
            </>
          )}
        </Box>
      </Box>

      {/* Confirm dialog */}
      <Dialog open={!!confirm} onClose={hideConfirm} PaperProps={{ sx: { borderRadius: 4, bgcolor: PANEL_BG } }}>
        <DialogTitle sx={{ color: 'white', pb: 0 }}>{confirm?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', pt: 1 }}>
            {confirm?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={hideConfirm} sx={{ color: MUTED, textTransform: 'none' }}>
            {tr.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={() => { confirm?.onConfirm(); hideConfirm(); }}
            sx={{ bgcolor: DEFAULT_THEME_COLOR, borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#2aad8a' } }}
          >
            {tr.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = state => ({
  currentUser: state.common.currentUser,
  context: state.common.context,
  recordingFreakRoute: state.common.recordingFreakRoute,
});

const mapDispatchToProps = dispatch => ({
  setBottomNavigationVisible: isVisible => dispatch({ type: BOTTOM_NAVIGATION_VISIBILITY, payload: isVisible }),
  setTopNavigationVisible: isVisible => dispatch({ type: TOP_NAVIGATION_VISIBILITY, payload: isVisible }),
  setRecordingFreakRoute: route => dispatch({ type: RECORDING_FREAK_ROUTE, payload: route }),
});

export default connect(mapStateToProps, mapDispatchToProps)(FreakTrackerView);
